import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GCC_ODC_TABS, getTabIndex } from "../managed-service-tabs";

@Component({
  selector: 'app-gccodcsolutions',
  templateUrl: './gccodcsolutions.component.html',
  styleUrls: ['./gccodcsolutions.component.scss']
})
export class GccodcsolutionsComponent implements OnInit {

  constructor(private activatedRoute: ActivatedRoute, private router: Router) { }
  
  tabName: string = "ODC Setup & Scaling";
  tabs = GCC_ODC_TABS;
  tabIndex: number = 1;

  slideConfig = {
    slidesToShow: 3,
    slidesToScroll: 1,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 1500,
    arrows: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: true,
          centerMode: true,
          centerPadding: '40px',
          slidesToShow: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          arrows: true,
          centerMode: true,
          centerPadding: '40px',
          slidesToShow: 1
        }
      }
    ]
  };

  odcSetupHelp = [
    {
      title: 'ODC Planning & Scope Definition',
      description: 'Clarify the purpose of your offshore centre, the work it will own, the capabilities required and the outcomes expected from the engagement.'
    },
    {
      title: 'Team Structure & Capability Planning',
      description: 'Identify the engineering roles, skills, team composition and leadership requirements appropriate to your delivery scope.'
    },
    {
      title: 'Delivery Framework & Collaboration',
      description: 'Define practical workflows for planning, development, reviews, reporting, communication and coordination with your onshore or global teams.'
    },
    {
      title: 'Initial Setup & Mobilisation',
      description: 'Coordinate agreed activities to establish the team, working practices, technical collaboration and delivery routines.'
    },
    {
      title: 'Scaling & Continuous Improvement',
      description: `Support expansion of the team's size or scope while strengthening delivery consistency, knowledge sharing and operational alignment.`
    }
  ];

  odcSetupSteps = ['Assess', 'Plan', 'Mobilise', 'Stabilise', 'Scale'];

  odcSetupSuitableFor = [
    'Organisations starting offshore engineering operations in India',
    'Businesses expanding an existing offshore development team',
    'Technology companies seeking a focused, dedicated delivery capability',
    'Enterprises looking to improve coordination between onshore and offshore teams'
  ];

  // images live at assets/images/gcc-odc/1.1.jpeg ... 1.10.jpeg
  odcSetupSlides = Array(10);

  gccCapabilityFocus = [
    {
      title: 'Capability Assessment & Roadmap',
      description: `Understand current strengths, capability gaps, business priorities and opportunities to expand the centre's contribution.`
    },
    {
      title: 'Engineering & Technical Ownership',
      description: 'Build the skills and practices needed to take greater responsibility for applications, platforms, architecture, engineering quality and technical outcomes.'
    },
    {
      title: 'Product & Platform Capabilities',
      description: 'Support the development of teams that contribute to product roadmaps, platform evolution, technical improvements and ongoing engineering decisions.'
    },
    {
      title: 'Data, Cloud & AI Capabilities',
      description: 'Identify relevant opportunities to develop expertise in data engineering, cloud platforms, intelligent automation and AI-enabled engineering, based on business needs.'
    },
    {
      title: 'Knowledge & Collaboration Practices',
      description: 'Strengthen documentation, knowledge sharing, cross-location collaboration and working relationships between India-based teams and global stakeholders.'
    },
    {
      title: 'Capability Maturity & Measurement',
      description: 'Define practical indicators of progress, such as ownership of defined components, delivery quality, cycle time, reuse of engineering assets and contribution to business objectives.'
    }
  ];

  gccCapabilitySteps = [
    {title: 'Execute', description: 'Deliver clearly defined work reliably.'},
    {title: 'Own', description: 'Take responsibility for defined systems, components or outcomes.'},
    {title: 'Integrate', description: 'Contribute directly to global planning and cross-functional priorities.'},
    {title: 'Innovate', description: 'Develop and improve solutions, platforms and reusable capabilities that create measurable value.'}
  ];

  gccCapabilitySuitableFor = [
    'Existing ODCs ready to expand beyond execution-focused work',
    'GCC leaders seeking to strengthen technical ownership',
    'Enterprises developing product, platform, data, cloud or AI capabilities in India',
    'Organisations seeking a more measurable connection between centre activities and global business priorities'
  ];

  // images live at assets/images/gcc-odc/2.1.jpeg ... 2.9.jpeg
  gccCapabilitySlides = Array(9);

  dedicatedTeamsCapabilities = [
    {
      title: 'Product & Application Engineering',
      description: 'Support application development, feature delivery, enhancements and ongoing product engineering.'
    },
    {
      title: 'Cloud & Platform Engineering',
      description: 'Develop capabilities for cloud environments, platform development, infrastructure automation and DevOps practices, as required by the engagement.'
    },
    {
      title: 'Data Engineering',
      description: 'Build teams focused on data pipelines, integration, processing and the engineering foundations for analytics and AI initiatives.'
    },
    {
      title: 'Quality Engineering & Testing',
      description: 'Strengthen software quality through test engineering, automation, validation and quality practices integrated into delivery workflows.'
    },
    {
      title: 'AI & Intelligent Automation',
      description: 'Develop engineering capabilities for appropriate AI-enabled solutions and workflow automation, with attention to security, reliability and human oversight.'
    },
    {
      title: 'Technical Leadership & Architecture',
      description: `Define technical leadership and architecture responsibilities appropriate to the team's scope, complexity and level of ownership.`
    }
  ];

  dedicatedTeamsSteps = [
    {title: 'Understand', description: 'Understand your objectives, technical environment and delivery expectations.'},
    {title: 'Define', description: 'Define the roles, skills, team structure and responsibilities.'},
    {title: 'Assemble', description: 'Assemble the agreed team and establish working arrangements.'},
    {title: 'Integrate', description: 'Integrate the team with your tools, stakeholders and engineering processes.'},
    {title: 'Improve', description: 'Review performance and adjust capabilities as your priorities evolve.'}
  ];

  dedicatedTeamsSuitableFor = [
    'Product companies expanding engineering capacity',
    'Enterprises developing or modernising applications and platforms',
    'Organisations establishing a new offshore engineering team',
    'Technology leaders who need specialist skills or additional delivery capacity',
    'Businesses building longer-term engineering capabilities in India'
  ];

  // images live at assets/images/gcc-odc/3.1.jpeg ... 3.7.jpeg
  dedicatedTeamsSlides = Array(7);

  operatingModelImprovements = [
    {
      title: 'Governance & Decision Rights',
      description: 'Clarify responsibilities, escalation paths, decision-making authority and the relationship between centre leadership and global stakeholders.'
    },
    {
      title: 'Organisation & Accountability',
      description: `Align team structures and responsibilities with the centre's mandate, technology domains and expected outcomes.`
    },
    {
      title: 'Delivery Processes & Ways of Working',
      description: 'Improve planning, prioritisation, engineering workflows, dependencies, quality practices and coordination across locations.'
    },
    {
      title: 'Performance & Outcome Measurement',
      description: 'Develop meaningful measures of delivery performance, engineering quality, capability growth and business contribution—not just headcount or activity.'
    },
    {
      title: 'Global Integration & Stakeholder Alignment',
      description: 'Improve collaboration between India-based teams, global technology functions, product leaders and relevant business stakeholders.'
    },
    {
      title: 'Knowledge, Automation & Continuous Improvement',
      description: 'Strengthen knowledge management, reduce avoidable manual work and identify opportunities to improve efficiency through appropriate automation and AI-enabled practices.'
    }
  ];

  operatingModelSteps = [
    {title: 'Assess', description: 'Understand the current model, friction points and desired outcomes.'},
    {title: 'Align', description: `Agree on the centre's mandate and the changes that matter most.`},
    {title: 'Redesign', description: 'Define appropriate responsibilities, governance and working practices.'},
    {title: 'Implement', description: 'Introduce agreed improvements in manageable steps.'},
    {title: 'Review', description: 'Measure results, gather feedback and refine the approach.'}
  ];

  operatingModelSuitableFor = [
    'ODCs that have grown beyond their original delivery mandate',
    'GCCs expanding into new technology domains or business functions',
    'Organisations experiencing coordination or accountability challenges',
    'Centre leaders seeking stronger alignment with global business priorities',
    'Enterprises preparing their India operations for the next stage of growth'
  ];

  // images live at assets/images/gcc-odc/4.1.jpeg ... 4.8.jpeg
  operatingModelSlides = Array(8);

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe((param) => {
      this.tabIndex = getTabIndex(this.tabs, param.get("tab"));
      this.tabName = this.tabs[this.tabIndex - 1].name;
    });
  }

  title(i: number) {
    this.tabName = this.tabs[i - 1].name;
    this.router.navigate(["gcc-odc-solutions", this.tabs[i - 1].slug]);
  }
}
